import { useCallback, useEffect, useId, useState } from "react";
import { Accept, useDropzone } from "react-dropzone";
import { cadValidator } from "shared/constants/officeFiles";
import { FileType } from "shared/utils/enum";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";

interface UploadSize {
  width?: number | string;
  height?: number | string;
}

interface CustomFileUploadProps {
  onFileChange?: (file: File | null, preview: string | null) => void;
  error?: string | null;
  onRemove?: () => void;
  accept?: Accept;
  initialPreview?: string;
  previewType?: string;
  showPreview?: boolean;
  acceptImages?: boolean;
  acceptFiles?: boolean;
  maxSize?: number;
  size?: "sm" | "md" | "lg" | UploadSize;
  showChangePhotoButton?: boolean;
  onChangePhoto?: () => void;
}

const CustomFileUpload = ({
  onFileChange,
  error,
  onRemove,
  accept,
  initialPreview,
  previewType,
  size,
  showPreview = true,
  acceptImages = true,
  acceptFiles = true,
  maxSize = 50 * 1024 * 1024,
  showChangePhotoButton = false,
  onChangePhoto,
}: CustomFileUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const uniqueId = useId();

  const uploadSizeStyle = (() => {
    if (!size) return undefined;

    if (typeof size === "string") {
      switch (size) {
        case "sm":
          return { width: 120, height: 120 };
        case "md":
          return { width: 180, height: 180 };
        case "lg":
          return { width: 260, height: 245 };
        default:
          return undefined;
      }
    }

    return {
      width: size.width,
      height: size.height,
      minWidth: size.width,
      minHeight: size.height,
      maxWidth: size.width,
      maxHeight: size.height,
    };
  })();

  const isSmallSize =
    uploadSizeStyle &&
    uploadSizeStyle.width === 80 &&
    uploadSizeStyle.height === 80;

  const getDefaultAccept = (): Accept => {
    if (accept) return accept;

    const acceptObj: Accept = {};

    if (acceptImages) {
      acceptObj["image/*"] = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    }

    if (acceptFiles) {
      acceptObj["application/pdf"] = [".pdf"];
      acceptObj["application/msword"] = [".doc"];
      acceptObj[
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ] = [".docx"];
      acceptObj["application/vnd.ms-excel"] = [".xls"];
      acceptObj[
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ] = [".xlsx"];
      acceptObj["text/*"] = [".txt", ".csv"];
    }

    if (!acceptImages && !acceptFiles) {
      return {};
    }

    return acceptObj;
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const selectedFile = acceptedFiles[0];
      if (selectedFile) {
        if (selectedFile.size > maxSize) return;

        const previewUrl = URL.createObjectURL(selectedFile);

        setFile(selectedFile);
        setPreview(previewUrl);

        if (onFileChange) {
          onFileChange(selectedFile, previewUrl);
        }
      }
    },
    [onFileChange, maxSize],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    onDrop,
    accept: getDefaultAccept(),
    validator: cadValidator(getDefaultAccept() as Record<string, string[]>),
    multiple: false,
    maxSize: maxSize,
    noClick: false,
  });

  const {
    getRootProps: getImageRootProps,
    getInputProps: getImageInputProps,
    open: openImageFileDialog,
  } = useDropzone({
    onDrop,
    accept: getDefaultAccept(),
    validator: cadValidator(getDefaultAccept() as Record<string, string[]>),
    multiple: false,
    maxSize: maxSize,
    noClick: false,
  });

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    if (initialPreview) {
      setPreview(initialPreview);
    }
  }, [initialPreview]);

  const isImageFile = (file: File | null): boolean => {
    if (!file) return false;
    return file.type.startsWith("image/");
  };

  const isImagePreview =
    preview && (file ? isImageFile(file) : previewType === FileType.IMAGE);

  return (
    <div className={styles.uploadContainer}>
      {!preview && (
        <div
          className={classNames(
            styles.uploadArea,
            styles.responsiveUpload,
            isDragActive && styles.dragActive,
            isDragReject && styles.dragReject,
            isSmallSize && styles.smallSize,
          )}
          style={uploadSizeStyle}
          {...getRootProps()}
        >
          <input id={`fileInput-${uniqueId}`} {...getInputProps()} />
          <div className={styles.uploadPlaceholder}>
            <Icons.ImgUpload className={styles.uploadIconPlaceholder} />
            <p className={classNames(styles.addImage, "hidden sm:block")}>
              Add file
            </p>
          </div>
        </div>
      )}

      {showPreview && preview && (
        <div
          className={classNames(
            styles.previewContainer,
            showChangePhotoButton && styles.fullSizePreview,
            isSmallSize && styles.smallPreviewSize,
          )}
        >
          <div
            className={classNames(
              showChangePhotoButton
                ? styles.fullSizeImageContainer
                : "flex items-center gap-2",
              !showChangePhotoButton &&
                isImagePreview &&
                styles.imageThumbnailContainer,
            )}
            {...(isImagePreview && !showChangePhotoButton
              ? getImageRootProps()
              : {})}
          >
            {isImagePreview && !showChangePhotoButton && (
              <input
                id={`fileInput-replace-${uniqueId}`}
                {...getImageInputProps()}
              />
            )}
            {isImagePreview && showChangePhotoButton && (
              <input
                id={`fileInput-replace-${uniqueId}`}
                {...getImageInputProps()}
                style={{ display: "none" }}
              />
            )}
            {file ? (
              <div
                className={
                  showChangePhotoButton ? styles.fullSizeImageWrapper : ""
                }
              >
                {file?.type.includes("image") && (
                  <img
                    src={preview || ""}
                    alt={file?.name || "Uploaded"}
                    className={
                      showChangePhotoButton ? styles.fullSizeImage : undefined
                    }
                  />
                )}

                {file?.type.includes("video") && (
                  <video src={preview} height={50} width={50} />
                )}

                {!file?.type.includes("image") &&
                  !file?.type.includes("video") && (
                    <div className={styles.documentPreview}>
                      <Icons.FilePdfIcon className={styles.fileTypeThumb} />
                      <span
                        className={styles.documentName}
                        title={file?.name}
                      >
                        {file?.name}
                      </span>
                    </div>
                  )}

                {!showChangePhotoButton && isImageFile(file) && (
                  <label className={styles.fileName} title={file?.name}>
                    {file?.name || `file.${file?.name.split(".").pop()}`}
                  </label>
                )}
              </div>
            ) : (
              <>
                {isImagePreview && preview && (
                  <div
                    className={
                      showChangePhotoButton ? styles.fullSizeImageWrapper : ""
                    }
                  >
                    <img
                      src={preview}
                      alt="Uploaded"
                      className={classNames(
                        showChangePhotoButton
                          ? styles.fullSizeImage
                          : styles.imageThumbnail,
                      )}
                    />
                  </div>
                )}
                {!showChangePhotoButton && !isImagePreview && (
                  <label className={styles.fileName}>
                    file
                    {previewType === FileType.IMAGE
                      ? ".png"
                      : previewType === FileType.VIDEO
                        ? ".mp4"
                        : previewType === FileType.PDF
                          ? ".pdf"
                          : ""}
                  </label>
                )}
              </>
            )}
          </div>

          {showChangePhotoButton && isImagePreview && (
            <div
              className={classNames(
                styles.uploadBorder,
                "flex items-center justify-center gap-2 cursor-pointer",
              )}
              onClick={() => {
                openImageFileDialog();
                if (onChangePhoto) {
                  onChangePhoto();
                }
              }}
            >
              <Icons.CameraIcon2 className={styles.cameraIcon} />
              <span>Change Photo</span>
            </div>
          )}
        </div>
      )}

      <div className="flex items-start w-full justify-start">
        {fileRejections.length > 0 && (
          <p className={"error text-left"}>UnSupported file format</p>
        )}
        {error && <p className={"error text-start"}>{error}</p>}
      </div>
    </div>
  );
};

export default CustomFileUpload;
